import { Button, DatePicker, Input, message } from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';

import ProductImage from '../../components/productImage';
import PPResizableTable from '../../components/table/PPResizableTable';
import { isHandledByInterceptor } from '../../utils/apiError';
import { getIndexColumn } from '../../utils/getIndexColumn';
import ReportFilterBar, { ReportField } from './ReportFilterBar';
import { useCachedState } from './reportCache';
import { findMissingDateError, getExportUrl, startDownload } from './reportRequest';
import './style.less';

/** Тайлан гаргаж эхэлсэн огноо — үүнээс өмнөх өдрийг сонгуулахгүй */
const FIRST_REPORT_DATE = dayjs('2025-12-17');

const ProductStockReport = () => {
  // Таб солиод буцаж ирэхэд хайлтын нөхцөл, үр дүн хэвээр үлдэнэ
  const [date, setDate] = useCachedState('stock:date', null);
  const [tableData, setTableData] = useCachedState('stock:rows', []);
  const [pagination, setPagination] = useCachedState('stock:pagination', {
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useCachedState('stock:filters', {});
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const searchInput = useRef(null);

  const fetchData = async (page = 1, pageSize = pagination.pageSize, filterParams = filters) => {
    /**
     * Огноогүйгээр хайлт явуулахгүй. Өмнө нь огноог цэвэрлээд «Хайх» дарахад
     * хүсэлт огноогүй явж, хүснэгтэд өмнөх үр дүн хэвээр үлддэг тул хуучин
     * огноогоор хайсан мэт харагддаг байв.
     */
    const dateError = findMissingDateError(date);
    if (dateError) {
      message.warning(dateError);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/1.0/request/inventory-report', {
        date: date?.format('YYYY-MM-DD'),
        page,
        pageSize,
        // Хоосон шүүлтүүрийг хүсэлтэд оруулахгүй
        ...Object.fromEntries(Object.entries(filterParams).filter(([, value]) => value)),
      });

      if (response.status === 200) {
        setTableData(response.data.result.docs || []);
        if (response.data.result.totalDocs === 0) {
          message.warning('Тайлан байхгүй байна.');
        }
        setPagination({
          current: response.data.result.page ?? page,
          pageSize: response.data.result.pageSize ?? pageSize,
          total: response.data.result.totalDocs ?? 0,
        });
      }
    } catch (error) {
      // Мэдэгдлийг axios-ийн global interceptor гаргана — энд давхардуулахгүй
      console.error('Inventory report error: ', error);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    const dateError = findMissingDateError(date);
    if (dateError) {
      message.warning(dateError);
      return;
    }

    setExporting(true);
    try {
      const response = await axios.post('/api/1.0/request/inventory-report-excel', {
        date: date.format('YYYY-MM-DD'),
      });

      const url = getExportUrl(response);
      /**
       * Өгөгдөл байхгүй үед ч «амжилттай татагдлаа» гэж хэлдэг байсан тул
       * «Тайлан байхгүй байна»-тай зэрэг гарч зөрчилддөг байв.
       */
      if (!url) {
        // Серверийн алдааг interceptor мэдэгдсэн бол «байхгүй» гэж давхардуулахгүй
        if (!isHandledByInterceptor(response.data)) message.warning('Татах тайлан байхгүй байна.');
        return;
      }

      startDownload(url);
      message.success('Excel файл амжилттай татагдлаа.');
    } catch (error) {
      console.error('Inventory report export error: ', error);
    } finally {
      setExporting(false);
    }
  };

  const handleTableChange = (paginationConfig, tableFilters) => {
    const filterParams = {
      productTitle: tableFilters.producttitle?.[0],
      productSku: tableFilters.productsku?.[0],
      wareHouseCode: tableFilters.warehousecode?.[0],
      positionCode: tableFilters.positioncode?.[0],
    };

    setFilters(filterParams);
    fetchData(paginationConfig.current, paginationConfig.pageSize, filterParams);
  };

  const getColumnSearchProps = (title, filterKey) => ({
    // Хадгалсан шүүлтүүрийг (таб солиод буцаж ирэхэд) багана дээр нь сэргээнэ
    filteredValue: filters[filterKey] ? [filters[filterKey]] : null,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`${title} хайх`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            size="small"
            style={{ width: 90 }}
            icon={<SearchOutlined />}
            onClick={() => confirm()}
          >
            Хайх
          </Button>
          <Button size="small" style={{ width: 90 }} onClick={() => clearFilters()}>
            Цэвэрлэх
          </Button>
        </div>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
  });

  const columns = [
    getIndexColumn(pagination.current, pagination.pageSize),
    {
      title: 'Барааны нэр',
      dataIndex: 'producttitle',
      key: 'producttitle',
      width: 240,
      ...getColumnSearchProps('Барааны нэр', 'productTitle'),
    },
    {
      title: 'Барааны SKU Код',
      dataIndex: 'productsku',
      key: 'productsku',
      width: 220,
      ...getColumnSearchProps('SKU Код', 'productSku'),
    },
    {
      title: 'Агуулах',
      dataIndex: 'warehousecode',
      key: 'warehousecode',
      width: 140,
      ...getColumnSearchProps('Агуулах', 'wareHouseCode'),
    },
    {
      title: 'Зураг',
      dataIndex: 'productimage',
      key: 'productimage',
      width: 100,
      render: (url) => (
        <ProductImage src={url} size={44} style={{ objectFit: 'cover', borderRadius: 6 }} />
      ),
    },
    { title: 'Үлдэгдэл', dataIndex: 'stock', key: 'stock', width: 130 },
    { title: 'Боломжит үлдэгдэл', dataIndex: 'availablestock', key: 'availablestock', width: 170 },
  ];

  return (
    <div className="pp_table_wrapper">
      <ReportFilterBar
        onSearch={() => fetchData(1)}
        loading={loading}
        actions={
          <Button
            className="all_buttons"
            icon={<DownloadOutlined />}
            onClick={exportExcel}
            loading={exporting}
          >
            Export
          </Button>
        }
      >
        <ReportField label="Огноо сонгох">
          <DatePicker
            className="date-picker all_buttons"
            value={date}
            onChange={setDate}
            disabledDate={(current) => current && current.isBefore(FIRST_REPORT_DATE, 'day')}
          />
        </ReportField>
      </ReportFilterBar>

      <PPResizableTable
        rowKey={(record, index) => record?._id ?? index}
        columns={columns}
        dataSource={tableData}
        loading={loading}
        size="middle"
        onChange={handleTableChange}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          pageSizeOptions: ['10', '20', '50', '100'],
          showSizeChanger: true,
          showTotal: (total) => `Нийт ${total} илэрц`,
        }}
      />
    </div>
  );
};

export default ProductStockReport;
